import React, { useState, useEffect } from "react";
import { NetworkManager } from "../../Components/NetworkManager";
import { CommonUtil } from "../../utils/CommonUtil";
import { Constants } from "../Languages/Constants";
import ToastManager from "../../Components/ToastManager";
import CheckAvailabilityPreview from "./CheckAvailabilityPreview";
import GenericPopup from "../../Components/GenericPopup";

interface BillingConfigurationsProps {
    reservationId: string | number;
    reservationStatus?: string;
    onSaveSuccess?: () => void;
    isMarketplace?: boolean;
    equipType?: string;
}

const BillingConfigurations: React.FC<BillingConfigurationsProps> = ({ 
    reservationId, 
    reservationStatus,
    onSaveSuccess,
    isMarketplace,
    equipType
}) => {
    console.log("Rendering BillingConfigurations for reservationId:", reservationId, "equipType:", equipType);
    const [billingConfig, setBillingConfig] = useState<any>(null);
    const [isEditingBilling, setIsEditingBilling] = useState(false);
    const [billingPreviewData, setBillingPreviewData] = useState<any>(null);
    const [showBillingPreview, setShowBillingPreview] = useState(false);
    const [billingForm, setBillingForm] = useState<any>({});

    useEffect(() => {
        getBillingConfig();
    }, [reservationId, reservationStatus]);

    const getBillingConfig = () => {
        NetworkManager.sendJsonRequest(`api/reservation/edit/${reservationId}`, "", "", "GET", true, (data: any) => {
            const parsed = CommonUtil.parseData(data);
            setBillingConfig(parsed);
            setBillingForm(parsed);
            setIsEditingBilling(false);
        }, (error: any) => {
            console.error("Error fetching billing config:", error);
        });
    }

    const onBillingFormChange = (e: any) => {
        const { name, value, type } = e.target;
        setBillingForm((prev: any) => ({
            ...prev,
            [name]: type === 'number' ? (value === '' ? null : Number(value)) : value
        }));
    };

    const onSaveBilling = () => {
        const changedFields: any = {};
        Object.keys(billingForm).forEach(key => {
            if (billingForm[key] !== billingConfig[key]) {
                changedFields[key] = billingForm[key];
            }
        });

        NetworkManager.sendJsonRequest(`api/reservation/edit/${reservationId}`, changedFields, "", "PUT", true, (data: any) => {
            const parsed = CommonUtil.parseData(data);
            setBillingConfig(parsed);
            setBillingForm(parsed);
            setIsEditingBilling(false);
            ToastManager.showToast(CommonUtil.getMessageText(Constants.SAVED_SUCCESSFULLY, "Saved Successfully"), "Info");
            if (onSaveSuccess) onSaveSuccess();
        }, (error: any) => {
            // Error handled by NetworkManager typically, but can add specific handling if needed
        });
    };

    const onPreviewBilling = () => {
        NetworkManager.sendJsonRequest(`api/reservation/edit/preview/${reservationId}`, billingForm, "", "POST", true, (data: any) => {
            const parsed = CommonUtil.parseData(data);
            console.log(parsed, "parsed")
            setBillingPreviewData(parsed);
            setShowBillingPreview(true);
        }, (error: any) => {
            // Error handled by NetworkManager
        });
    };

    if (!billingConfig) return null;

    return (
        <>
            <section className="p-4" style={{ backgroundColor: isEditingBilling ? '#f8f9fa' : '#ffffff', borderBottom: '1px solid #dee2e6' }}>
                <div className="d-flex mb-3">
                    <h6 className="m-0">{CommonUtil.getMessageText(Constants.BILLING_CONFIGURATIONS, "Billing Configurations")}</h6>
                    <span className="small text-muted ms-1 mt-1">{isMarketplace ? "(Marketplace)" : "(Rental Company)"}</span>
                </div>
                <div className="row">
                    {isMarketplace && (
                        <>
                            {/* Marketplace Fields */}
                            <div className="col-md-4">
                                <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.DAILY_RATE, "Daily Rate")}</label>
                                {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                    <input
                                        type="number"
                                        name="dailyRate"
                                        className="form-control form-control-sm"
                                        value={billingForm?.dailyRate ?? ''}
                                        onChange={onBillingFormChange}
                                    />
                                ) : (
                                    <div className="font-weight-bold">{billingConfig?.dailyRate != null ? `${billingConfig?.dailyRate}` : '—'}</div>
                                )}
                            </div>

                            <div className="col-md-4">
                                <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.WEEKLY_RATE, "Weekly Rate")}</label>
                                {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                    <input
                                        type="number"
                                        name="weeklyRate"
                                        className="form-control form-control-sm"
                                        value={billingForm?.weeklyRate ?? ''}
                                        onChange={onBillingFormChange}
                                    />
                                ) : (
                                    <div className="font-weight-bold">{billingConfig?.weeklyRate != null ? `${billingConfig?.weeklyRate}` : '—'}</div>
                                )}
                            </div>

                            <div className="col-md-4">
                                <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.MONTHLY_RATE, "Monthly Rate")}</label>
                                {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                    <input
                                        type="number"
                                        name="monthlyRate"
                                        className="form-control form-control-sm"
                                        value={billingForm?.monthlyRate ?? ''}
                                        onChange={onBillingFormChange}
                                    />
                                ) : (
                                    <div className="font-weight-bold">{billingConfig?.monthlyRate != null ? `${billingConfig?.monthlyRate}` : '—'}</div>
                                )}
                            </div>
                        </>
                    )}
                </div>
                <div className="row mt-3">
                    {isMarketplace ? (
                        <>
                            <div className="col-md-4">
                                <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.DISTANCE_RATE, "Distance Rate")}</label>
                                {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                    <input
                                        type="number"
                                        name="distanceRate"
                                        className="form-control form-control-sm"
                                        value={billingForm?.distanceRate ?? ''}
                                        onChange={onBillingFormChange}
                                    />
                                ) : (
                                    <div className="font-weight-bold">{billingConfig?.distanceRate != null ? `${billingConfig?.distanceRate}` : '—'}</div>
                                )}
                            </div>

                            <div className="col-md-4">
                                <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.ESTIMATED_DISTANCE_PER_DAY, "Est. Distance / Day")}</label>
                                {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                    <input
                                        type="number"
                                        name="estimatedDistancePerDay"
                                        className="form-control form-control-sm"
                                        value={billingForm?.estimatedDistancePerDay ?? ''}
                                        onChange={onBillingFormChange}
                                    />
                                ) : (
                                    <div className="font-weight-bold">{billingConfig?.estimatedDistancePerDay != null ? `${billingConfig?.estimatedDistancePerDay} /${billingConfig?.distanceUnit ?? '—'}` : '—'}</div>
                                )}
                            </div>

                            {equipType === 'REEFER VAN' && (
                                <div className="col-md-4">
                                    <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.ENGINE_RATE, "Engine Rate")}</label>
                                    {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                        <input
                                            type="number"
                                            name="engineRate"
                                            className="form-control form-control-sm"
                                            value={billingForm?.engineRate ?? ''}
                                            onChange={onBillingFormChange}
                                        />
                                    ) : (
                                        <div className="font-weight-bold">{billingConfig?.engineRate != null ? `${billingConfig?.engineRate}` : '—'}</div>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="col-md-4">
                                    <label className="small text-muted d-block mb-1">Rental Monthly Rate</label>
                                    {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                    <input
                                        type="number"
                                        name="monthlyRate"
                                        className="form-control form-control-sm"
                                        value={billingForm?.monthlyRate ?? ''}
                                        onChange={onBillingFormChange}
                                    />
                                ) : (
                                    <div className="font-weight-bold">{billingConfig?.monthlyRate != null ? `${billingConfig?.monthlyRate}` : '—'}</div>
                                )}
                            </div>
                            <div className="col-md-4">
                                <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.RENTAL_BILLING_LOGIC, "Rental Billing Logic")}</label>
                                {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                    <select
                                        name="rentalRateBillingLogic"
                                        className="form-control form-control-sm"
                                        value={billingForm?.rentalRateBillingLogic || ''}
                                        onChange={onBillingFormChange}
                                    >
                                        <option value="Fixed">{CommonUtil.getMessageText(Constants.FIXED, "Fixed")}</option>
                                        <option value="Pro Rated">{CommonUtil.getMessageText(Constants.PRO_RATED, "Pro Rated")}</option>
                                    </select>
                                ) : (
                                    <div className="font-weight-bold">{billingConfig?.rentalRateBillingLogic || '-'}</div>
                                )}
                            </div>
                            <div className="col-md-4">
                                <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.RENTAL_REFUND_LOGIC, "Rental Refund Logic")}</label>
                                {isEditingBilling ? (
                                    <select
                                        name="rentalRateRefundLogic"
                                        className="form-control form-control-sm"
                                        value={billingForm?.rentalRateRefundLogic || ''}
                                        onChange={onBillingFormChange}
                                    >
                                        <option value="Refund">Refund</option>
                                        <option value="Non Refund">Non Refund</option>
                                    </select>
                                ) : (
                                    <div className="font-weight-bold">{billingConfig?.rentalRateRefundLogic || '-'}</div>
                                )}
                            </div>

                        </>
                    )}
                </div>

                <div className="row mt-3">
                    {/* Billing Logic Section */}
                    {!isMarketplace && (
                        <>
                            <div className="col-md-4">
                                <label className="small text-muted d-block mb-1">Distance Monthly Rate</label>
                                {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                    <input
                                        type="number"
                                        name="monthlyDistanceRate"
                                        className="form-control form-control-sm"
                                        value={billingForm?.monthlyDistanceRate ?? ''}
                                        onChange={onBillingFormChange}
                                    />
                                ) : (
                                    <div className="font-weight-bold">{billingConfig?.monthlyDistanceRate != null ? `${billingConfig?.monthlyDistanceRate}` : '—'}</div>
                                )}
                            </div>

                            <div className="col-md-4">
                                <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.DISTANCE_BILLING_LOGIC, "Distance Billing Logic")}</label>
                                {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                    <select
                                        name="distanceRateBillingLogic"
                                        className="form-control form-control-sm"
                                        value={billingForm?.distanceRateBillingLogic || ''}
                                        onChange={onBillingFormChange}
                                    >
                                        <option value="Fixed">{CommonUtil.getMessageText(Constants.FIXED, "Fixed")}</option>
                                        <option value="Actual">{CommonUtil.getMessageText(Constants.ACTUAL, "Actual")}</option>
                                    </select>
                                ) : (
                                    <div className="font-weight-bold">{billingConfig?.distanceRateBillingLogic || '-'}</div>
                                )}
                            </div>
                            <div className="col-md-4">
                                <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.DISTANCE_REFUND_LOGIC, "Distance Refund Logic")}</label>
                                {isEditingBilling ? (
                                    <select
                                        name="distanceRateRefundLogic"
                                        className="form-control form-control-sm"
                                        value={billingForm?.distanceRateRefundLogic || ''}
                                        onChange={onBillingFormChange}
                                    >
                                        <option value="Refund">Refund</option>
                                        <option value="Non Refund">Non Refund</option>
                                    </select>
                                ) : (
                                    <div className="font-weight-bold">{billingConfig?.distanceRateRefundLogic || '-'}</div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {!isMarketplace && equipType === 'REEFER VAN' && (<div className="row mt-3">
                        <>
                                <div className="col-md-4">
                                    <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.ENGINE_MONTHLY_RATE, "Engine Monthly Rate")}</label>
                                    {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                        <input
                                            type="number"
                                            name="engineMonthlyRate"
                                            className="form-control form-control-sm"
                                            value={billingForm?.engineMonthlyRate ?? ''}
                                            onChange={onBillingFormChange}
                                        />
                                    ) : (
                                        <div className="font-weight-bold">{billingConfig?.engineMonthlyRate != null ? `${billingConfig?.engineMonthlyRate}` : '—'}</div>
                                    )}
                                </div>
                                <div className="col-md-4">
                                    <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.ENGINE_BILLING_LOGIC, "Engine Billing Logic")}</label>
                                    {isEditingBilling && !billingConfig?.invoicesGenerated ? (
                                        <select
                                            name="engineRateBillingLogic"
                                            className="form-control form-control-sm"
                                            value={billingForm?.engineRateBillingLogic || ''}
                                            onChange={onBillingFormChange}
                                        >
                                            <option value="Fixed">{CommonUtil.getMessageText(Constants.FIXED, "Fixed")}</option>
                                            <option value="Actual">{CommonUtil.getMessageText(Constants.ACTUAL, "Actual")}</option>
                                        </select>
                                    ) : (
                                        <div className="font-weight-bold">{billingConfig?.engineRateBillingLogic || '-'}</div>
                                    )}
                                </div>
                                <div className="col-md-4">
                                    <label className="small text-muted d-block mb-1">{CommonUtil.getMessageText(Constants.ENGINE_REFUND_LOGIC, "Engine Refund Logic")}</label>
                                    {isEditingBilling ? (
                                        <select
                                            name="engineRateRefundLogic"
                                            className="form-control form-control-sm"
                                            value={billingForm?.engineRateRefundLogic || ''}
                                            onChange={onBillingFormChange}
                                        >
                                            <option value="Refund">Refund</option>
                                            <option value="Non Refund">Non Refund</option>
                                        </select>
                                    ) : (
                                        <div className="font-weight-bold">{billingConfig?.engineRateRefundLogic || '-'}</div>
                                    )}
                                </div>
                            </>
                        </div>
                    )}

                {!(billingConfig?.invoicesGenerated && isMarketplace) && CommonUtil.isRootUser() && (
                    <div className="mt-4 d-flex justify-content-end gap-2">
                        {isEditingBilling && (
                            <>
                                <button className="btn btn-info" onClick={onPreviewBilling}>
                                    {CommonUtil.getMessageText(Constants.PREVIEW_INVOICES, "Preview Invoices")}
                                </button>
                                <button className="btn btn-primary" onClick={onSaveBilling}>
                                    {CommonUtil.getMessageText(Constants.SAVE, "Save")}
                                </button>
                            </>
                        )}
                        {!billingConfig?.isDeactivated && reservationStatus !== 'CANCELLED' && reservationStatus !== CommonUtil.RESERVATION_DELETED && (
                            <button
                                className={`btn ${isEditingBilling ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                                onClick={() => setIsEditingBilling(!isEditingBilling)}
                            >
                                {isEditingBilling ? CommonUtil.getMessageText(Constants.CANCEL, "Cancel") : CommonUtil.getMessageText(Constants.EDIT, "Edit")}
                            </button>
                        )}
                    </div>
                )}
            </section>

            {showBillingPreview && (
                <GenericPopup
                    header={CommonUtil.getMessageText(Constants.PREVIEW_INVOICES, "Preview Invoices")}
                    mode={showBillingPreview}
                    onClose={() => setShowBillingPreview(false)}
                    fullscreen={true}
                >
                    <div style={{ backgroundColor: '#f5f5f5', padding: '1rem' }}>
                        <CheckAvailabilityPreview data={billingPreviewData} />
                    </div>
                </GenericPopup>
            )}
        </>
    );
};

export default BillingConfigurations;
